import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Search, FileText, PenTool } from "lucide-react";
import { MarketingHeader } from "@/components/marketing/marketing-header";
import { MarketingFooter } from "@/components/marketing/marketing-footer";
import { seoMeta } from "@/lib/seo/head";
import { JsonLd } from "@/components/seo/json-ld";
import { SITE, abs } from "@/lib/seo/site";

export const Route = createFileRoute("/features")({
  head: () => seoMeta({
    title: "Funzionalità - LexAIPro",
    description:
      "Tre pilastri pensati per il professionista legale: ricerca AI con citazioni verificabili, analisi documenti, redazione assistita.",
    path: "/features",
  }),
  component: FeaturesHub,
});

const FEATURES_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Funzionalità LexAIPro",
  description: "Ricerca legale AI, analisi documenti e redazione assistita per professionisti legali italiani.",
  url: abs("/features"),
  provider: { "@type": "Organization", name: SITE.name, url: SITE.url },
  areaServed: { "@type": "Country", name: "Italia" },
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Pilastri LexAIPro",
    itemListElement: [
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Ricerca legale AI", description: "Domande in linguaggio naturale su normativa e giurisprudenza con citazioni verificabili.", url: abs("/funzionalita/ricerca-legale-ai") } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Analisi documenti", description: "Sintesi, estrazione clausole e classificazione del rischio su PDF, DOCX e scansioni.", url: abs("/funzionalita/analisi-documenti") } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Redazione assistita", description: "Atti, contratti e pareri da istruzioni in italiano con 40+ template.", url: abs("/funzionalita/redazione-assistita") } },
    ],
  },
};

const PILLARS = [
  {
    icon: Search,
    title: "Ricerca legale AI",
    desc: "Domande in linguaggio naturale su normativa e giurisprudenza. Risposte strutturate con fonti verificabili.",
    to: "/funzionalita/ricerca-legale-ai",
  },
  {
    icon: FileText,
    title: "Analisi documenti",
    desc: "PDF, DOCX, scansioni. Sintesi, estrazione clausole con classificazione del rischio, confronto versioni.",
    to: "/funzionalita/analisi-documenti",
  },
  {
    icon: PenTool,
    title: "Redazione assistita",
    desc: "Atti, contratti, pareri da istruzioni in italiano. 40+ template, citazioni normative inserite in automatico.",
    to: "/funzionalita/redazione-assistita",
  },
] as const;

function FeaturesHub() {
  return (
    <div className="min-h-screen bg-background">
      <JsonLd data={FEATURES_SCHEMA} />
      <MarketingHeader />
      <main className="pt-32 pb-24">
        <div className="mx-auto max-w-5xl px-6">
          <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Funzionalità</p>
          <h1 className="mt-3 font-display text-4xl font-semibold tracking-tight sm:text-5xl">
            Tre pilastri per esercitare meglio.
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground">
            LexAIPro non sostituisce l'avvocato. Amplifica il giudizio professionale eliminando il lavoro ripetitivo.
          </p>

          <div className="mt-12 grid gap-4 md:grid-cols-3">
            {PILLARS.map((p) => {
              const Icon = p.icon;
              return (
                <Link
                  key={p.to}
                  to={p.to}
                  className="group rounded-2xl border border-border bg-card p-6 transition hover:-translate-y-1 hover:border-foreground/30 hover:shadow-lg"
                >
                  <Icon className="h-6 w-6 text-foreground/80" />
                  <h2 className="mt-4 font-display text-xl font-semibold">{p.title}</h2>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-foreground">
                    Approfondisci <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
                  </span>
                </Link>
              );
            })}
          </div>

          <div className="mt-16 flex flex-wrap items-center gap-3">
            <Link
              to="/signup"
              className="inline-flex h-11 items-center rounded-full bg-foreground px-6 text-sm font-medium text-background hover:opacity-90"
            >
              Inizia gratis - 14 giorni
            </Link>
            <Link
              to="/demo"
              className="inline-flex h-11 items-center rounded-full border border-border px-6 text-sm font-medium hover:bg-muted"
            >
              Guarda la demo
            </Link>
          </div>
        </div>
      </main>
      <MarketingFooter />
    </div>
  );
}
